import axios from "axios";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const API_BASE_URL = import.meta.env.VITE_CORE_API_URL;

export interface Company {
  id: string;
  slug: string;
  name: string;
  logo_url?: string | null;
  rtu?: string | null;
  rtu_url?: string | null;
  industry_id: string;
  size: string;
  phone: string;
  website: string;
  address: string;
  notes: string;
  attachments: any[];
  parent_company_id?: string | null;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
  industry?: {
    id: string;
    name: string;
  };
  parent_company?: Company | null;
  subsidiaries?: Company[];
}

export interface CreateCompanyData {
  slug: string;
  name: string;
  logo_url?: string;
  rtu?: string;
  rtu_url?: string;
  industry_id: string;
  size: string;
  phone: string;
  website: string;
  address: string;
  notes: string;
  parent_company_id?: string | null;
}

export interface UpdateCompanyData extends Partial<CreateCompanyData> {}

export interface CompaniesResponse {
  success: boolean;
  data: Company[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CompanyResponse {
  success: boolean;
  data: Company;
}

export interface QueryParams {
  slug: string;
  page?: number;
  limit?: number;
  search?: string;
  industry_id?: string;
  size?: string;
  parent_company_id?: string;
}

export const companyAPI = {
  /**
   * Obtiene lista de empresas con paginación y filtros
   */
  getCompanies: async (params: QueryParams): Promise<CompaniesResponse> => {
    try {
      const queryParams = new URLSearchParams();

      // Parámetro obligatorio
      queryParams.append("slug", params.slug);

      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.industry_id)
        queryParams.append("industry_id", params.industry_id);
      if (params.size) queryParams.append("size", params.size);
      if (params.parent_company_id)
        queryParams.append("parent_company_id", params.parent_company_id);

      const response = await api.get(
        `${API_BASE_URL}/client-companies?${queryParams.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Caso 1: Respuesta directa (formato nuevo)
      if (response.data.success && Array.isArray(response.data.data)) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination || {
            total: response.data.data.length,
            page: params.page || 1,
            limit: params.limit || 50,
            totalPages: 1,
          },
        };
      }
      // Caso 2: Respuesta anidada (formato AWS Lambda proxy)
      else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        return {
          success: true,
          data: response.data.body.data,
          pagination: response.data.body.pagination,
        };
      } else {
        throw new Error(
          response.data.body?.message ||
            response.data.message ||
            "Error al obtener empresas",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene una empresa por ID
   */
  getCompanyById: async (
    id: string,
    slug: string,
  ): Promise<CompanyResponse> => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/client-companies/${id}?slug=${slug}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Caso 1: Respuesta directa
      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data,
        };
      }
      // Caso 2: Respuesta anidada
      else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        return {
          success: true,
          data: response.data.body.data,
        };
      } else {
        throw new Error(
          response.data.body?.message ||
            response.data.message ||
            "Error al obtener la empresa",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Crea una nueva empresa
   */
  createCompany: async (
    companyData: CreateCompanyData,
  ): Promise<CompanyResponse> => {
    try {
      const response = await api.post(
        `${API_BASE_URL}/client-companies`,
        companyData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.statusCode === 201 && response.data.body) {
        console.log("Caso 1: statusCode 201 (Created)");
        const companyData = response.data.body.data || response.data.body;
        return {
          success: true,
          data: companyData,
        };
      }

      if (response.data.statusCode === 200 && response.data.body?.success) {
        console.log("Caso 2: statusCode 200 + body");
        return {
          success: true,
          data: response.data.body.data,
        };
      }

      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      if (response.data.id && response.data.name) {
        return {
          success: true,
          data: response.data,
        };
      }

      if (response.data.statusCode === 200 && response.data.body?.data) {
        return {
          success: true,
          data: response.data.body.data,
        };
      }

      if (response.data.data) {
        return {
          success: true,
          data: response.data.data,
        };
      }
      if (
        response.data.message &&
        response.data.message.includes("exitosamente")
      ) {
        throw new Error(
          "La empresa se creó pero el servidor no devolvió los datos completos. Por favor, recarga la página.",
        );
      }
      throw new Error(
        response.data.body?.message ||
          response.data.message ||
          "Error al crear la empresa",
      );
    } catch (error: any) {
      console.error("Error en createCompany:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        throw new Error(
          error.response.data?.body?.message ||
            error.response.data?.message ||
            "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Actualiza una empresa existente
   */

  updateCompany: async (
    id: string,
    companyData: UpdateCompanyData,
  ): Promise<CompanyResponse> => {
    try {
      const { slug, ...updateData } = companyData;

      if (!slug) {
        throw new Error("El slug es requerido para actualizar la empresa");
      }

      const url = `${API_BASE_URL}/client-companies/${id}?slug=${slug}`;

      console.log("📡 [companyService] PUT request to:", url);
      console.log("📦 [companyService] Payload:", updateData);

      const response = await api.put(url, updateData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("✅ [companyService] Response:", response.data);

      // Caso 1: Respuesta directa
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }
      // Caso 2: Respuesta anidada
      else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        return {
          success: true,
          data: response.data.body.data,
        };
      } else {
        throw new Error(
          response.data.body?.message ||
            response.data.message ||
            "Error al actualizar la empresa",
        );
      }
    } catch (error: any) {
      console.error("Error en updateCompany:", error);

      if (error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Elimina una empresa
   */
  deleteCompany: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(
        `${API_BASE_URL}/client-companies/${id}?slug=${slug}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Caso 1: Respuesta directa
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Empresa eliminada exitosamente",
        };
      }
      // Caso 2: Respuesta anidada
      else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        return {
          success: true,
          message:
            response.data.body.message || "Empresa eliminada exitosamente",
        };
      } else {
        throw new Error(
          response.data.body?.message ||
            response.data.message ||
            "Error al eliminar la empresa",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene empresas subsidiarias de una empresa padre
   */
  getSubsidiaries: async (
    parentCompanyId: string,
    slug: string,
  ): Promise<CompaniesResponse> => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/client-companies?slug=${slug}&parent_company_id=${parentCompanyId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Caso 1: Respuesta directa
      if (response.data.success && Array.isArray(response.data.data)) {
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination,
        };
      }
      // Caso 2: Respuesta anidada
      else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        return {
          success: true,
          data: response.data.body.data,
          pagination: response.data.body.pagination,
        };
      } else {
        throw new Error(
          response.data.body?.message ||
            response.data.message ||
            "Error al obtener subsidiarias",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message || "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene URL para subir logo
   */
  getLogoUploadUrl: async (fileName: string, contentType: string) => {
    try {
      const companyDataString = localStorage.getItem("company");
      if (!companyDataString) throw new Error("No company data found");
      const companyData = JSON.parse(companyDataString);
      const slug = companyData.slug;
      
      const response = await api.post(`/companies/${slug}/logo-upload-url`, {
        slug,
        fileName,
        contentType
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || "Error al obtener URL de subida de logo");
    }
  },

  /**
   * Subir archivo a S3 usando la URL prefirmada
   */
  uploadToS3: async (uploadUrl: string, file: File) => {
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type
        }
      });
      return true;
    } catch (error: any) {
      throw new Error("Error al subir el archivo a S3");
    }
  },

  /**
   * Obtiene configuración general de la empresa
   */
  getSettings: async () => {
    try {
      const companyDataString = localStorage.getItem("company");
      if (!companyDataString) throw new Error("No company data found");
      const companyData = JSON.parse(companyDataString);
      const slug = companyData.slug;
      
      const response = await api.get(`/companies/${slug}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || "Error al obtener configuración de empresa");
    }
  },

  /**
   * Actualiza configuración general de la empresa
   */
  updateSettings: async (settingsData: {
    name?: string;
    currency?: string;
    email?: string;
    phone?: string;
    website?: string;
    address?: string;
    primary_color?: string;
    secondary_color?: string;
    logo_url?: string;
  }) => {
    try {
      const companyDataString = localStorage.getItem("company");
      if (!companyDataString) throw new Error("No company data found");
      const companyData = JSON.parse(companyDataString);
      const slug = companyData.slug;

      const response = await api.put(`/companies/${slug}`, {
        slug,
        ...settingsData
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || "Error al actualizar la configuración de la empresa");
    }
  },

  /**
   * Sube un archivo adjunto para una empresa (ej. RTU)
   */
  uploadAttachment: async (
    id: string,
    slug: string,
    file: File
  ): Promise<{ success: boolean; url: string }> => {
    try {
      // 1. Obtener URL pre-firmada
      const getUrlResponse = await api.post(
        `${API_BASE_URL}/client-companies/upload-url`,
        { id, slug, fileName: file.name, contentType: file.type }
      );
      
      const { upload_url, file_url } = getUrlResponse.data.data;

      // 2. Subir directamente a S3
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type
        }
      });

      // 3. Actualizar la empresa con la URL de archivo
      await api.put(
        `${API_BASE_URL}/client-companies/${id}`,
        { slug, rtu_url: file_url }
      );

      return { success: true, url: file_url };
    } catch (error: any) {
      throw new Error(error.message || "Error al subir archivo adjunto de empresa");
    }
  },
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    const publicEndpoints = ["/login"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.endsWith(endpoint),
    );

    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      response.data.statusCode === 200 &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);
